import { useSyncExternalStore } from 'react'

/**
 * Светлая и тёмная темы.
 *
 * Выбор игрока хранится и побеждает системную настройку; пока выбора нет,
 * тема берётся из системы. Значение проставляется атрибутом на <html> —
 * стили описаны так, что страница выглядит правильно ещё до того, как
 * отработает скрипт, и не моргает при загрузке.
 *
 * Внешним хранилищем, а не useState, — как вид поля и громкость рядом.
 * Раньше тема жила состоянием в App, и оттуда её приходилось передавать
 * пропсами каждому, кто её показывает. С появлением страницы настроек таких
 * мест стало два, а копия в каждом хуке разъехалась бы: страница настроек
 * переключила бы свою, а шапка осталась бы при своей.
 *
 * Атрибут ставится здесь же, а не эффектом в компоненте: тема должна стоять
 * на странице с первого кадра, а не с первого рендера того, кто про неё
 * знает. Отсюда и явный импорт модуля в main.tsx.
 */
export type Theme = 'light' | 'dark'

const KEY = 'ludo.theme'

function preferred(): Theme {
  try {
    const saved = localStorage.getItem(KEY)

    if (saved === 'light' || saved === 'dark') return saved
  } catch {
    // Приватный режим: тема просто не запомнится.
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

let current: Theme = preferred()
const listeners = new Set<() => void>()

function apply(theme: Theme): void {
  document.documentElement.dataset.theme = theme
}

apply(current)

function subscribe(listener: () => void): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export function setTheme(next: Theme): void {
  if (next === current) return

  current = next
  apply(next)

  try {
    localStorage.setItem(KEY, next)
  } catch {
    // см. preferred()
  }

  for (const listener of listeners) listener()
}

export function toggleTheme(): void {
  setTheme(current === 'dark' ? 'light' : 'dark')
}

export function useTheme(): Theme {
  // Третий аргумент — значение для сервера: рендера на сервере в проекте нет,
  // но без него useSyncExternalStore падает в тестовой среде.
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => current,
  )
}
